import { Color, Typography } from '@persona-id/design-tokens';
import { useState, VFC } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import ReactSelect from 'dashboard/components/ReactSelect';
import { FormCreateReportData } from 'dashboard/forms/FormCreateReport';
import FormCreateReportAddressLookup from 'dashboard/forms/FormCreateReportAddressLookup';
import FormCreateReportAdverseMedia from 'dashboard/forms/FormCreateReportAdverseMedia';
import FormCreateReportBusinessAdverseMedia from 'dashboard/forms/FormCreateReportBusinessAdverseMedia';
import FormCreateReportBusinessClassification from 'dashboard/forms/FormCreateReportBusinessClassification';
import FormCreateReportBusinessLookup from 'dashboard/forms/FormCreateReportBusinessLookup';
import FormCreateReportBusinessNonprofit from 'dashboard/forms/FormCreateReportBusinessNonprofit';
import FormCreateReportBusinessOnlinePresence from 'dashboard/forms/FormCreateReportBusinessOnlinePresence';
import FormCreateReportBusinessSocialMedia from 'dashboard/forms/FormCreateReportBusinessSocialMedia/FormCreateReportBusinessSocialMedia';
import FormCreateReportBusinessWatchlist from 'dashboard/forms/FormCreateReportBusinessWatchlist';
import FormCreateReportCryptoAddressWatchlist from 'dashboard/forms/FormCreateReportCryptoAddressWatchlist';
import FormCreateReportEmailAddress from 'dashboard/forms/FormCreateReportEmailAddress';
import FormCreateReportFinraBrokerCheck from 'dashboard/forms/FormCreateReportFinraBrokerCheck';
import FormCreateReportPhoneNumber from 'dashboard/forms/FormCreateReportPhoneNumber';
import FormCreateReportPoliticallyExposedPerson from 'dashboard/forms/FormCreateReportPoliticallyExposedPerson';
import FormCreateReportProfile from 'dashboard/forms/FormCreateReportProfile';
import FormCreateReportProfileNonauthoritative from 'dashboard/forms/FormCreateReportProfileNonAuthoritative';
import FormCreateReportScreening from 'dashboard/forms/FormCreateReportScreening';
import FormCreateReportSecActionLookup from 'dashboard/forms/FormCreateReportSecActionLookup';
import FormCreateReportSentilinkScores from 'dashboard/forms/FormCreateReportSentilinkScores';
import FormCreateReportSocialMedia from 'dashboard/forms/FormCreateReportSocialMedia';
import FormCreateReportSynthetic from 'dashboard/forms/FormCreateReportSynthetic';
import FormCreateReportWatchlist from 'dashboard/forms/FormCreateReportWatchlist';
import {
  FormCsvUploadCreateReport,
  FormCsvUploadCreateReportData,
} from 'dashboard/forms/FormCsvUploadCreateReport';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import {
  ModalBody,
  ModalContainer,
  ModalDescription,
  ModalHeader,
} from 'dashboard/layouts/ModalLayout';
import { ReportTemplateFilterOption } from 'dashboard/models/report-template';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

interface CreateReportModalProps {
  reportTemplateFilterOptions: ReportTemplateFilterOption[];
  selectedReportTemplateFilterOption?: ReportTemplateFilterOption;
  showCsvUploadTab: boolean;
  hideReferenceId: boolean;
  initialValues?: Partial<FormCreateReportData>;
  handleClose: () => void;
  handleCreateReport: (
    data: FormCreateReportData,
    reportTemplateFilterOption: ReportTemplateFilterOption,
  ) => Promise<void>;
  handleCsvUploadCreateReports?: (
    data: FormCsvUploadCreateReportData,
    reportTemplateFilterOption: ReportTemplateFilterOption,
  ) => Promise<void>;
  handleReportTemplateFilterOptionSelect: (reportTemplateId?: string) => void;
  readOnly?: boolean;
}

const SupportedReportTemplateTypes = [
  ApiModel.ReportTemplateAddressLookup,
  ApiModel.ReportTemplateAdverseMedia,
  ApiModel.ReportTemplateBusinessAdverseMedia,
  ApiModel.ReportTemplateBusinessAssociatedPersons,
  ApiModel.ReportTemplateBusinessClassification,
  ApiModel.ReportTemplateBusinessLookup,
  ApiModel.ReportTemplateBusinessNonprofit,
  ApiModel.ReportTemplateBusinessOnlinePresence,
  ApiModel.ReportTemplateBusinessSocialMedia,
  ApiModel.ReportTemplateBusinessWatchlist,
  ApiModel.ReportTemplateCryptoAddressWatchlist,
  ApiModel.ReportTemplateEmailAddress,
  ApiModel.ReportTemplateFinraBrokerCheck,
  ApiModel.ReportTemplatePhoneNumber,
  ApiModel.ReportTemplateProfile,
  ApiModel.ReportTemplateProfileNonAuthoritative,
  ApiModel.ReportTemplatePoliticallyExposedPerson,
  ApiModel.ReportTemplateScreening,
  ApiModel.ReportTemplateSecActionLookup,
  ApiModel.ReportTemplateSocialMedia,
  ApiModel.ReportTemplateSynthetic,
  ApiModel.ReportTemplateWatchlist,
];

const TabContainer = styled('div')`
  width: 50%;
  display: flex;
  flex-direction: column;
`;

const Tab = styled('div').attrs(() => ({ role: 'button' }))<{ selected?: boolean }>`
  ${Typography.TextMediumBold};
  color: ${({ selected }) => (selected ? Color.CorePurple800 : 'grey')};
  height: ${SPACINGS.base * 4}px;
  text-align: center;
  cursor: pointer;
`;

const CurvedSelectionBar = styled('div')<{ visible?: boolean }>`
  border-top: 1px solid ${Color.CorePurple800};
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  border-top-left-radius: 2px;
  border-top-right-radius: 2px;
`;

const Tabs = styled('div')`
  display: flex;
  border-bottom: 1px solid grey;
`;

const TemplateFormControlLabel = styled(FormControlLabel)`
  padding-top: ${SPACINGS.base * 2}px;
`;

const CreateReportModal: VFC<CreateReportModalProps> = ({
  reportTemplateFilterOptions,
  selectedReportTemplateFilterOption,
  hideReferenceId,
  handleClose,
  handleCreateReport,
  handleCsvUploadCreateReports,
  handleReportTemplateFilterOptionSelect,
  showCsvUploadTab,
  initialValues,
  readOnly,
}: CreateReportModalProps) => {
  const [manualInput, setManualInput] = useState(true);
  const allowCreateProfileReport = useFeatureFlag(
    'dashboard:enable-create-reports-profile-reports',
  );
  const supportedReportTemplateFilterOptions = reportTemplateFilterOptions
    .filter((rt) => SupportedReportTemplateTypes.includes(rt.type))
    .filter((rt) => rt.type !== ApiModel.ReportTemplateProfile || allowCreateProfileReport)
    .filter((rt) => !rt.archived);
  const handleManualClick = setManualInput.bind(null, true);
  const handleUploadClick = setManualInput.bind(null, false);

  return (
    <ModalContainer>
      <ModalHeader data-test='create-report-modal__header' onClose={handleClose}>
        Create a Report
      </ModalHeader>
      <ModalDescription>
        Need access to additional report templates? Please{' '}
        <Link to='/dashboard/contact-us'>contact us</Link>.
      </ModalDescription>
      <ModalBody>
        {supportedReportTemplateFilterOptions.length > 0 && (
          <FormGroup>
            {showCsvUploadTab && (
              <Tabs>
                <TabContainer>
                  <Tab selected={manualInput} onClick={handleManualClick}>
                    Manual input
                  </Tab>
                  <CurvedSelectionBar visible={manualInput} />
                </TabContainer>
                <TabContainer>
                  <Tab selected={!manualInput} onClick={handleUploadClick}>
                    Upload CSV
                  </Tab>
                  <CurvedSelectionBar visible={!manualInput} />
                </TabContainer>
              </Tabs>
            )}
            <TemplateFormControlLabel>Template</TemplateFormControlLabel>
            <ReactSelect
              value={selectedReportTemplateFilterOption?.id}
              onChange={handleReportTemplateFilterOptionSelect}
              id='create-report-modal__report-template-selector'
              data-test='create-report-modal__report-template-selector'
              options={supportedReportTemplateFilterOptions.map((rt) => ({
                label: rt.name,
                value: rt.id,
              }))}
              portal={true}
            />
          </FormGroup>
        )}
        {selectedReportTemplateFilterOption && (
          <>
            {manualInput ? (
              <FormCreateReportForType
                selectedReportTemplateFilterOption={selectedReportTemplateFilterOption}
                handleClose={handleClose}
                handleCreateReport={handleCreateReport}
                hideReferenceId={hideReferenceId}
                initialValues={initialValues}
                readOnly={readOnly}
              />
            ) : (
              <FormCsvUploadCreateReport
                reportTemplateFilterOption={selectedReportTemplateFilterOption}
                onSubmit={(data) =>
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  handleCsvUploadCreateReports(data, selectedReportTemplateFilterOption)
                }
              />
            )}
          </>
        )}
      </ModalBody>
    </ModalContainer>
  );
};

interface FormCreateReportForTypeProps {
  selectedReportTemplateFilterOption: ReportTemplateFilterOption;
  hideReferenceId?: boolean;
  initialValues?: Partial<FormCreateReportData>;
  handleClose?: () => void;
  handleCreateReport?: (
    data: FormCreateReportData,
    reportTemplateFilterOption: ReportTemplateFilterOption,
  ) => Promise<void>;
  handleCsvUploadCreateReports?: (
    data: FormCsvUploadCreateReportData,
    reportTemplateFilterOption: ReportTemplateFilterOption,
  ) => Promise<void>;
  readOnly?: boolean;
}

const FormCreateReportForType = ({
  selectedReportTemplateFilterOption,
  handleClose,
  handleCreateReport,
  hideReferenceId,
  initialValues,
  readOnly,
}: FormCreateReportForTypeProps) => {
  const handleCreateReportWithSelectedReportTemplate = (data: FormCreateReportData) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    handleCreateReport(data, selectedReportTemplateFilterOption);
  };
  const allowCreateProfileReport = useFeatureFlag(
    'dashboard:enable-create-reports-profile-reports',
  );

  switch (selectedReportTemplateFilterOption.type) {
    case ApiModel.ReportTemplateAddressLookup: {
      return (
        <FormCreateReportAddressLookup
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          hideReferenceId={hideReferenceId}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateAdverseMedia: {
      return (
        <FormCreateReportAdverseMedia
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          hideReferenceId={hideReferenceId}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateBusinessAdverseMedia: {
      return (
        <FormCreateReportBusinessAdverseMedia
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateBusinessClassification: {
      return (
        <FormCreateReportBusinessClassification
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleSubmit={handleCreateReportWithSelectedReportTemplate}
          defaultValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateBusinessLookup: {
      return (
        <FormCreateReportBusinessLookup
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateBusinessNonprofit: {
      return (
        <FormCreateReportBusinessNonprofit
          handleCancel={handleClose}
          // TODO (ivan): Migrate all of the reports to react-hook-form
          // this is needed to clean up the typing to make it strict
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleSubmit={handleCreateReportWithSelectedReportTemplate}
          defaultValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateBusinessOnlinePresence: {
      return (
        <FormCreateReportBusinessOnlinePresence
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleSubmit={handleCreateReportWithSelectedReportTemplate}
          defaultValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateBusinessSocialMedia: {
      return (
        <FormCreateReportBusinessSocialMedia
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateBusinessWatchlist: {
      return (
        <FormCreateReportBusinessWatchlist
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateCryptoAddressWatchlist: {
      return (
        <FormCreateReportCryptoAddressWatchlist
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          hideReferenceId={hideReferenceId}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateEmailAddress: {
      return (
        <FormCreateReportEmailAddress
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          hideReferenceId={hideReferenceId}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateFinraBrokerCheck: {
      return (
        <FormCreateReportFinraBrokerCheck
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          hideReferenceId={hideReferenceId}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateProfile: {
      if (!allowCreateProfileReport) {
        return null;
      }
      return (
        <FormCreateReportProfile
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          hideReferenceId={hideReferenceId}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateProfileNonAuthoritative: {
      return (
        <FormCreateReportProfileNonauthoritative
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          hideReferenceId={hideReferenceId}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplatePhoneNumber: {
      return (
        <FormCreateReportPhoneNumber
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          hideReferenceId={hideReferenceId}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplatePoliticallyExposedPerson: {
      return (
        <FormCreateReportPoliticallyExposedPerson
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          hideReferenceId={hideReferenceId}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateScreening: {
      return (
        <FormCreateReportScreening
          handleCancel={handleClose}
          /* @ts-expect-error TODO(maggie): fix this later */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          hideReferenceId={hideReferenceId}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateSecActionLookup: {
      return (
        <FormCreateReportSecActionLookup
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          hideReferenceId={hideReferenceId}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateSentilinkScores: {
      return (
        <FormCreateReportSentilinkScores
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          hideReferenceId={hideReferenceId}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateSocialMedia: {
      return (
        <FormCreateReportSocialMedia
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          hideReferenceId={hideReferenceId}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateSynthetic: {
      return (
        <FormCreateReportSynthetic
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          hideReferenceId={hideReferenceId}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateWatchlist: {
      return (
        <FormCreateReportWatchlist
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleCancel={handleClose}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onSubmit={handleCreateReportWithSelectedReportTemplate}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          hideReferenceId={hideReferenceId}
          initialValues={initialValues}
          readOnly={readOnly}
        />
      );
    }
    case ApiModel.ReportTemplateBusinessAssociatedPersons:
    case ApiModel.ReportTemplateBusinessLiensLookup:
    case ApiModel.ReportTemplateBusinessRegistrationsLookup:
    case ApiModel.ReportTemplateBusinessIndustryClassification:
    case ApiModel.ReportTemplateBusinessWebsite:
    case ApiModel.ReportTemplateChainalysisAddressScreening:
    case ApiModel.ReportTemplateClearbitBusinessLookup:
    case ApiModel.ReportTemplateCoinbaseCheckCryptoRisk:
    case ApiModel.ReportTemplateCustomList:
    case ApiModel.ReportTemplateEquifaxOneview:
    case ApiModel.ReportTemplateKyckrBusinessLookup:
    case ApiModel.ReportTemplateMiddeskBusinessLookup:
    case ApiModel.ReportTemplateMxAccount:
    case ApiModel.ReportTemplateSentilinkApplicationRisk:
    case ApiModel.ReportTemplateTrmWalletScreening: {
      return <div>This report type is currently not supported.</div>;
    }
    default:
      assertNever(selectedReportTemplateFilterOption.type);
  }
};

export default CreateReportModal;
